import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const INTEGRATIONS_FIELD_MAPPINGS_QUERY_KEY = 'integrations-field-mappings';

export function useGetExternalIntegrationFieldMappingsQuery({
	integrationProvider,
	objectType = 'Contact',
	impersonationContext,
	enabled = true,
	meta,
}: {
	integrationProvider: Api.IntegrationProvider;
	objectType?: string;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			// The API answers 404 rather than an empty array when nothing is mapped yet
			const mappings = await userSession.webServiceHelper.callAsync<Api.IIntegrationFieldMapping[]>({
				path: Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `integrations/${integrationProvider}/fieldMappings?objectType=${objectType}`,
				}),
				method: 'GET',
				throwOn404: false,
			});
			return mappings ?? [];
		},
		queryKey: [INTEGRATIONS_FIELD_MAPPINGS_QUERY_KEY, integrationProvider, objectType, impersonationContext],
		meta,
	});
}

export const invalidateFieldMappingsQuery = ({
	integrationProvider,
	objectType,
	impersonationContext,
}: {
	integrationProvider: Api.IntegrationProvider;
	objectType?: string;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const queryKey: unknown[] = [INTEGRATIONS_FIELD_MAPPINGS_QUERY_KEY, integrationProvider];
	if (objectType) {
		queryKey.push(objectType);
		if (impersonationContext) {
			queryKey.push(impersonationContext);
		}
	}
	return reactQueryClient.invalidateQueries({ queryKey });
};
